<template>
  <div :class="$style.toggle" role="tablist" :aria-label="t('aria')">
    <button
      v-for="opt in options"
      :key="opt.value"
      type="button"
      role="tab"
      :class="[$style.option, { [$style.active]: modelValue === opt.value }]"
      :aria-selected="modelValue === opt.value"
      @click="emit('update:modelValue', opt.value)"
    >
      {{ opt.label }}
    </button>
  </div>
</template>

<i18n lang="yaml">
ru:
  pick: Пикем
  result: Результат
  aria: Переключатель режима этапа
</i18n>

<script lang="ts" setup>
import { computed } from 'vue'

defineOptions({ name: 'PickemPickResultToggle' })

export type PickResultMode = 'pick' | 'result'

const props = defineProps<{
  modelValue: PickResultMode
}>()

const emit = defineEmits<{
  'update:modelValue': [value: PickResultMode]
}>()

const { t } = useI18n()

const options = computed<{ value: PickResultMode; label: string }[]>(() => [
  { value: 'pick', label: t('pick') },
  { value: 'result', label: t('result') }
])
</script>

<style lang="scss" module>
// По макету это не «pill»-toggle, а табы с underline'ом на общем
// bg-card фоне. Активный — accent-2 текст + accent-2 нижняя граница,
// неактивный — text-2 c opacity 0.5 и серая нижняя граница bg-border.
.toggle {
  display: inline-flex;
  align-items: stretch;
  background-color: color-get(--root-rgb-bg-card);
  border-radius: 0.8em;
  padding: 0 0.8em;
}

.option {
  appearance: none;
  background: transparent;
  border: 0;
  border-bottom: 1px solid color-get(--root-rgb-bg-border);
  color: color-get(--root-rgb-text-2);
  padding: 0.8em 1em;
  cursor: pointer;
  @include typo-get(subhead-m);
  display: inline-flex;
  align-items: center;
  gap: 0.6em;
  opacity: 0.5;
  transition:
    color 0.15s ease,
    border-color 0.15s ease,
    opacity 0.15s ease;

  @include hover-focus {
    opacity: 1;
    color: color-get(--root-rgb-text-1);
  }
}

.active {
  opacity: 1;
  border-bottom-color: color-get(--root-rgb-accent-2);
  color: color-get(--root-rgb-accent-2);
}

@include down(md) {
  .toggle {
    display: flex;
    width: 100%;
    padding: 0;
    border-radius: 0;
    background-color: transparent;
    border-bottom: 1px solid color-get(--root-rgb-bg-border);
    gap: 1.2em;
  }

  .option {
    padding: 1.2em 0;
    @include typo-get(label-m);
    opacity: 0.2;
  }

  .active {
    opacity: 1;
  }
}
</style>
